import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { openMobileAppInfoModal } from 'components/shared/MobileAppInfo';
import { showMobileInfoSelector } from 'store/app/selectors';
import { setToken } from 'utils/accessToken';
import axios, { setAuthToken } from 'utils/axios';
import { checkIsViewedByRedirectUri } from 'utils/mobileApplication';

const STATUS = {
  NOT_FOUND: 404,
  PASS_NEEDED: 401,
  JUST_VERIFY: 204,
};

export function useVerificationPage() {
  const [redirectUri] = useQueryParam('redirect_uri', StringParam);
  const { key } = useParams<{ key: string }>();
  const initialValues = useMemo(() => ({ key }), [key]);
  const [status, setStatus] = useState(0);
  const [showPass, setShowPass] = useState(false);
  const showMobileInfo = useSelector(showMobileInfoSelector);

  const checkAcc = useMutation(() =>
    axios
      .head('check_account_password_set', { params: { key } })
      .then((res) => {
        setStatus(res.status);
      })
      .catch((err) => {
        if (Axios.isAxiosError(err)) {
          setStatus(err.response?.status || 0);
        }
      }),
  );

  const verifyAcc = useMutation({
    mutationFn: (values: any) =>
      axios.post<{ accessToken: string; refreshToken: string; success: string }>('auth/verify-account', values),
    onError: () => {
      if (redirectUri) {
        location.href = redirectUri;
      }
    },
    onSuccess: (res) => {
      if (res.data.success) {
        setAuthToken(res.data.accessToken);
        setToken(res.data);

        if (redirectUri) {
          location.href = redirectUri;
        }

        if (showMobileInfo) {
          openMobileAppInfoModal(`verify-account?key=${key}`);
        }
      }
    },
  });

  const handleSubmit = useCallback(
    (values: any) => {
      verifyAcc.mutate(values);
    },
    [verifyAcc],
  );

  const error = useMemo(() => {
    if (status === STATUS.NOT_FOUND) {
      return 'Invalid token';
    }

    if (verifyAcc.error) {
      // @ts-ignore
      return verifyAcc.error.error;
    }

    return null;
  }, [status, verifyAcc.error]);

  useEffect(() => {
    checkAcc.mutate();
    // only after the first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ([0, STATUS.NOT_FOUND].includes(status)) return;

    if (status === STATUS.JUST_VERIFY) {
      verifyAcc.mutate({ key });
    }

    if (status === STATUS.PASS_NEEDED) {
      setShowPass(true);

      if (redirectUri && checkIsViewedByRedirectUri(redirectUri)) {
        return;
      }

      if (showMobileInfo) {
        openMobileAppInfoModal(`verify-account?key=${key}`);
      }
    }
    // only if status and showMobileInfo will be changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, showMobileInfo]);

  return {
    checkAcc,
    showPass,
    initialValues,
    handleSubmit,
    error,
    isSuccess: verifyAcc.isSuccess,
    verifying: verifyAcc.isLoading,
  };
}
